import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CALENDLY_URL = 'https://calendly.com/musesocial-info/discovery-call';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
};

const roles = [
  'Social Media Manager',
  'Content Creator',
  'Creative Strategist',
  'Influencer & Partnerships Manager',
  'Community Manager',
  'Video Editor',
  'Designer',
  'Creative Director',
  'Other',
];

export default function BookACallPage() {
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    company_size: '',
    hiring_roles: [],
    timeline: '',
    budget_range: '',
    additional_notes: '',
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleRole = (role) => {
    setFormData((prev) => ({
      ...prev,
      hiring_roles: prev.hiring_roles.includes(role)
        ? prev.hiring_roles.filter((r) => r !== role)
        : [...prev.hiring_roles, role],
    }));
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.company_name && formData.contact_name && formData.email;
      case 2: return formData.company_size && formData.hiring_roles.length > 0;
      case 3: return formData.timeline && formData.budget_range;
      default: return true;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/intake`, formData);
      setIsSubmitted(true);
      toast.success('Form submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    if (step === 0) { setStep(1); return; }
    if (step < 3) { setStep(step + 1); return; }
    handleSubmit();
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  if (isSubmitted) {
    return (
      <div className="pt-44 min-h-screen bg-background" data-testid="book-call-success">
        <div className="container-main py-16 md:py-24">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="max-w-3xl mx-auto text-center">
            <div className="w-20 h-20 bg-butter rounded-full flex items-center justify-center mx-auto mb-8">
              <Check className="h-10 w-10 text-foreground" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl font-medium mb-6">Thanks for reaching out!</h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-10">
              We've received your information. Go ahead and book your discovery call below.
            </p>
            <div className="border border-border rounded-sm overflow-hidden" data-testid="calendly-embed-container">
              <iframe
                src={CALENDLY_URL}
                title="Schedule a Discovery Call"
                width="100%"
                height="700"
                frameBorder="0"
                className="w-full"
                data-testid="calendly-iframe"
              />
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-44 min-h-screen bg-background" data-testid="book-call-page">
      <div className="container-main py-24 md:py-32">
        <div className="max-w-4xl mx-auto">
          {/* Landing state */}
          {step === 0 && (
            <motion.div initial="hidden" animate="visible" variants={fadeInUp} data-testid="book-call-landing">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <h1 className="font-serif text-4xl sm:text-5xl font-medium leading-tight mb-8">
                    Let's build your next phase of growth
                  </h1>
                  <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-4">
                    Tell us a bit about your brand and what you're looking to achieve.
                  </p>
                  <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-12">
                    From there, we'll walk you through how we can support you — whether through strategy, creative direction, or building your team.
                  </p>
                  <button
                    onClick={nextStep}
                    className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-8 py-3.5 rounded-full hover:bg-butter-dark transition-colors duration-300"
                    data-testid="continue-to-form-btn"
                  >
                    Continue to Book a Call <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
                <div className="hidden lg:block">
                  <img
                    src="/images/phone-hand.jpg"
                    alt="Social media strategy"
                    className="w-full h-[480px] object-cover"
                    data-testid="book-call-image"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* Form steps */}
          {step >= 1 && (
            <div className="max-w-2xl mx-auto">
              <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="mb-12">
                <p className="text-sm tracking-widest uppercase text-muted-foreground mb-4">Book a Strategy Call</p>
                <h1 className="font-serif text-2xl md:text-3xl font-medium mb-4">
                  Let's build your next phase of growth
                </h1>
              </motion.div>

              <div className="flex items-center gap-2 mb-12">
                {[1, 2, 3].map((s) => (
                  <div
                    key={s}
                    className={`h-1 flex-1 rounded-full transition-colors duration-300 ${s <= step ? 'bg-butter' : 'bg-border'}`}
                    data-testid={`progress-step-${s}`}
                  />
                ))}
              </div>

              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.div key="step1" initial="hidden" animate="visible" exit="exit" variants={fadeInUp} className="space-y-8" data-testid="form-step-1">
                    <h3 className="font-serif text-xl font-medium">Tell us about you</h3>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Company Name</label>
                        <Input
                          type="text" placeholder="Your company" value={formData.company_name}
                          onChange={(e) => updateField('company_name', e.target.value)}
                          className="border-0 border-b border-border rounded-none px-0 focus-visible:ring-0"
                          data-testid="input-company-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Your Name</label>
                        <Input
                          type="text" placeholder="Full name" value={formData.contact_name}
                          onChange={(e) => updateField('contact_name', e.target.value)}
                          className="border-0 border-b border-border rounded-none px-0 focus-visible:ring-0"
                          data-testid="input-contact-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Email Address</label>
                        <Input
                          type="email" placeholder="you@company.com" value={formData.email}
                          onChange={(e) => updateField('email', e.target.value)}
                          className="border-0 border-b border-border rounded-none px-0 focus-visible:ring-0"
                          data-testid="input-email"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div key="step2" initial="hidden" animate="visible" exit="exit" variants={fadeInUp} className="space-y-8" data-testid="form-step-2">
                    <h3 className="font-serif text-xl font-medium">What are you looking for?</h3>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Company Size</label>
                        <Select value={formData.company_size} onValueChange={(v) => updateField('company_size', v)}>
                          <SelectTrigger className="w-full" data-testid="select-company-size">
                            <SelectValue placeholder="Select company size" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-10">1-10 employees</SelectItem>
                            <SelectItem value="11-50">11-50 employees</SelectItem>
                            <SelectItem value="51-200">51-200 employees</SelectItem>
                            <SelectItem value="201-500">201-500 employees</SelectItem>
                            <SelectItem value="500+">500+ employees</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-4">Services you're interested in (select all)</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {roles.map((role) => (
                            <div key={role} className="flex items-center gap-3">
                              <Checkbox
                                id={role}
                                checked={formData.hiring_roles.includes(role)}
                                onCheckedChange={() => toggleRole(role)}
                                data-testid={`checkbox-role-${role.toLowerCase().replace(/\s+/g, '-')}`}
                              />
                              <label htmlFor={role} className="text-sm font-medium cursor-pointer">{role}</label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div key="step3" initial="hidden" animate="visible" exit="exit" variants={fadeInUp} className="space-y-8" data-testid="form-step-3">
                    <h3 className="font-serif text-xl font-medium">Timeline & Budget</h3>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Timeline</label>
                        <Select value={formData.timeline} onValueChange={(v) => updateField('timeline', v)}>
                          <SelectTrigger className="w-full" data-testid="select-timeline">
                            <SelectValue placeholder="When do you need support?" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asap">ASAP (within 2 weeks)</SelectItem>
                            <SelectItem value="1-month">Within 1 month</SelectItem>
                            <SelectItem value="1-3-months">1-3 months</SelectItem>
                            <SelectItem value="3-6-months">3-6 months</SelectItem>
                            <SelectItem value="exploring">Just exploring</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Budget Range</label>
                        <Select value={formData.budget_range} onValueChange={(v) => updateField('budget_range', v)}>
                          <SelectTrigger className="w-full" data-testid="select-budget">
                            <SelectValue placeholder="Approximate budget" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under-5k">Under $5k</SelectItem>
                            <SelectItem value="5k-10k">$5k - $10k</SelectItem>
                            <SelectItem value="10k-25k">$10k - $25k</SelectItem>
                            <SelectItem value="25k+">$25k+</SelectItem>
                            <SelectItem value="flexible">Flexible / Project-based</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Anything else? (Optional)</label>
                        <Textarea
                          placeholder="Tell us more about your goals..."
                          value={formData.additional_notes}
                          onChange={(e) => updateField('additional_notes', e.target.value)}
                          className="min-h-[120px] resize-none"
                          data-testid="textarea-notes"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex justify-between items-center mt-12 pt-8 border-t border-border">
                <button
                  onClick={prevStep}
                  className={`flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors duration-200 ${step === 1 ? 'invisible' : ''}`}
                  data-testid="btn-prev"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button
                  onClick={nextStep}
                  disabled={!canProceed() || isSubmitting}
                  className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-8 py-3 rounded-full hover:bg-butter-dark transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="btn-next"
                >
                  {isSubmitting ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</>
                  ) : step === 3 ? (
                    <>Submit</>
                  ) : (
                    <>Continue <ArrowRight className="h-4 w-4" /></>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
